/**
 * Core simulation engine for the insurance macro simulator
 * Full implementation with proper particle physics
 */
import { SandCircle } from './SandCircle.js';

export function initializeSimulation(container, onActivityAdd, onMarketDataUpdate) {
  const circles = {};
  
  // Pool configurations
  const pools = {
    1: { name: 'Retail Agencies', color: '#667eea', particles: 150 },
    2: { name: 'Insurance Carriers', color: '#764ba2', particles: 75 },
    3: { name: 'Wholesale / MGA / MGU', color: '#f093fb', particles: 60 },
    4: { name: 'Businesses', color: '#4facfe', particles: 150 },
    5: { name: 'People', color: '#43e97b', particles: 150 },
    6: { name: 'Reinsurance', color: '#ff6b6b', particles: 25 }
  };

  // Get circles container
  const circlesContainer = container.querySelector('#simulator-circles');
  if (!circlesContainer) {
    console.error('Circles container not found');
    return null;
  }

  // Position classes
  const positionClasses = {
    1: 'top-1/2 left-1/2 -translate-x-[450px] -translate-y-1/2',
    2: 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-[450px]',
    3: 'top-1/2 left-1/2 -translate-x-[550px] -translate-y-[280px]',
    4: 'top-1/2 left-[calc(50%+35px)] translate-x-0 -translate-y-1/2',
    5: 'top-1/2 left-[calc(50%-35px)] -translate-x-full -translate-y-1/2',
    6: 'top-1/2 left-1/2 -translate-x-1/2 translate-y-[350px]'
  };

  // Initialize each circle
  Object.keys(pools).forEach(poolId => {
    const pool = pools[poolId];
    
    const circleWrapper = document.createElement('div');
    circleWrapper.className = `absolute flex flex-col items-center gap-4 z-10 ${positionClasses[poolId]}`;
    circleWrapper.dataset.pool = poolId;

    const canvas = document.createElement('canvas');
    canvas.id = `circle${poolId}`;
    canvas.className = 'w-[200px] h-[200px] rounded-full bg-[#0a0a0a] border-2 border-[#222] cursor-pointer transition-all shadow-[0_0_20px_rgba(102,126,234,0.3)] hover:border-[#667eea] hover:shadow-[0_0_30px_rgba(102,126,234,0.5)] hover:scale-105';
    canvas.width = 200;
    canvas.height = 200;

    const label = document.createElement('div');
    label.className = 'text-[#666] text-[0.85rem] tracking-[0.5px] text-center whitespace-nowrap';
    label.textContent = pool.name;

    circleWrapper.appendChild(canvas);
    circleWrapper.appendChild(label);
    circlesContainer.appendChild(circleWrapper);

    // Create SandCircle instance
    const uniformSize = poolId == 5; // Only people have uniform size
    circles[poolId] = new SandCircle(
      canvas,
      pool.particles,
      pool.color,
      uniformSize,
      parseInt(poolId)
    );

    // Add click handler for activity
    canvas.addEventListener('click', () => {
      const now = new Date().toLocaleTimeString();
      onActivityAdd({
        type: 'premium',
        time: now,
        description: `Activity in ${pool.name}`,
        details: `Simulated event triggered`
      });
    });
  });

  // Draw connection lines
  drawConnectionLines(container);

  // Update market data periodically
  let updateCounter = 0;
  const updateInterval = setInterval(() => {
    if (!circles[1] || !circles[1].isAnimating) {
      clearInterval(updateInterval);
      return;
    }
    
    updateCounter++;
    if (updateCounter % 60 === 0) { // Every ~1 second
      const data = {
        people: { 
          particles: pools[5].particles, 
          premiums: Math.random() * 10000, 
          claims: Math.floor(Math.random() * 50) 
        },
        businesses: { 
          particles: pools[4].particles, 
          premiums: Math.random() * 25000, 
          claims: Math.floor(Math.random() * 30) 
        },
        agencies: { 
          particles: pools[1].particles, 
          commissions: Math.random() * 5000 
        },
        carriers: { 
          particles: pools[2].particles, 
          premiums: Math.random() * 50000, 
          claims: Math.floor(Math.random() * 100),
          profit: 60 + Math.random() * 20
        },
        wholesalers: { 
          particles: pools[3].particles, 
          commissions: Math.random() * 3000 
        },
        reinsurance: { 
          particles: pools[6].particles, 
          cedingPremiums: Math.random() * 15000, 
          recoveries: Math.random() * 8000 
        }
      };
      onMarketDataUpdate(data);
    }
  }, 16); // ~60fps

  // Return control interface
  return {
    pause: () => {
      Object.values(circles).forEach(circle => circle.pause());
    },
    resume: () => {
      Object.values(circles).forEach(circle => circle.resume());
    },
    cleanup: () => {
      clearInterval(updateInterval);
      Object.values(circles).forEach(circle => circle.stop());
      circlesContainer.innerHTML = '';
    }
  };
}

function drawConnectionLines(container) {
  const svg = container.querySelector('#connectionLines');
  if (!svg) return;

  const connections = [
    { from: 5, to: 1 },
    { from: 5, to: 2 },
    { from: 4, to: 1 },
    { from: 4, to: 2 },
    { from: 1, to: 2 },
    { from: 1, to: 3 },
    { from: 3, to: 2 },
    { from: 2, to: 6 }
  ];

  const svgRect = svg.getBoundingClientRect();

  connections.forEach(conn => {
    const fromCircle = container.querySelector(`[data-pool="${conn.from}"] canvas`);
    const toCircle = container.querySelector(`[data-pool="${conn.to}"] canvas`);

    if (fromCircle && toCircle) {
      const fromRect = fromCircle.getBoundingClientRect();
      const toRect = toCircle.getBoundingClientRect();

      const x1 = fromRect.left + fromRect.width / 2 - svgRect.left;
      const y1 = fromRect.top + fromRect.height / 2 - svgRect.top;
      const x2 = toRect.left + toRect.width / 2 - svgRect.left;
      const y2 = toRect.top + toRect.height / 2 - svgRect.top;

      const angle = Math.atan2(y2 - y1, x2 - x1);
      const radius = 110;
      const adjustedX1 = x1 + Math.cos(angle) * radius;
      const adjustedY1 = y1 + Math.sin(angle) * radius;
      const adjustedX2 = x2 - Math.cos(angle) * radius;
      const adjustedY2 = y2 - Math.sin(angle) * radius;

      const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
      line.setAttribute('x1', adjustedX1);
      line.setAttribute('y1', adjustedY1);
      line.setAttribute('x2', adjustedX2);
      line.setAttribute('y2', adjustedY2);
      line.setAttribute('stroke', '#444');
      line.setAttribute('stroke-width', '2');
      line.setAttribute('stroke-dasharray', '8,4');
      line.setAttribute('opacity', '0.5');
      line.setAttribute('marker-end', 'url(#arrowhead)');

      svg.appendChild(line);
    }
  });
}
